import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import BlogPostCard from './BlogPostCard';
import { WordPressPost } from '@/services/wordpress';

interface BlogContentProps {
  posts: WordPressPost[];
  isLoading: boolean;
  error: Error | null;
  searchTerm: string;
  selectedCategory: string;
  onClearFilters: () => void;
}

const BlogContent = ({ 
  posts, 
  isLoading, 
  error, 
  searchTerm, 
  selectedCategory, 
  onClearFilters 
}: BlogContentProps) => {
  // Loading State
  if (isLoading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <Skeleton className="aspect-video w-full" />
            <CardHeader>
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-6 w-full mb-2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-32 mb-4" />
              <div className="flex justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-24" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">
          Failed to load blog posts: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Check the browser console for more details.
        </p>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  // No Results
  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">
          No articles found. Try adjusting your search or category filter.
        </p>
        <Button onClick={onClearFilters}>
          Clear Filters
        </Button>
      </div>
    );
  }

  // Blog Posts Grid
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post) => (
        <BlogPostCard key={post.id} post={post} />
      ))}
    </div>
  );
};

export default BlogContent;